'use client'

import { useState, useContext } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/shadcn/dialog'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Textarea } from '@/components/ui/shadcn/textarea'
import { Label } from '@/components/ui/shadcn/label'
import { AlertCircle, Loader2 } from 'lucide-react'
import { AuthContext } from '@/contexts/AuthContext'
import axios from '@/lib/api/axios'
import {
  Users,
  Wrench,
  HardHat,
  Zap,
  Factory,
  ShieldAlert,
  Scale,
  CheckCircle2,
  Radio,
  ClipboardCheck,
  AlertTriangle,
  Flame,
  Settings,
  Activity,
  FileText,
  Phone,
  Wind,
  Droplets,
  Sun,
  Gauge,
} from 'lucide-react'

const PRESET_COLORS = [
  '#2563eb', // blue
  '#7c3aed', // purple
  '#db2777', // pink
  '#dc2626', // red
  '#ea580c', // orange
  '#ca8a04', // yellow
  '#16a34a', // green
  '#0891b2', // cyan
  '#6366f1', // indigo
  '#6b7280', // gray
]

const TEAM_ICONS = [
  { name: 'Users', component: Users },
  { name: 'Wrench', component: Wrench },
  { name: 'HardHat', component: HardHat },
  { name: 'Zap', component: Zap },
  { name: 'Factory', component: Factory },
  { name: 'ShieldAlert', component: ShieldAlert },
  { name: 'Scale', component: Scale },
  { name: 'CheckCircle2', component: CheckCircle2 },
  { name: 'Radio', component: Radio },
  { name: 'ClipboardCheck', component: ClipboardCheck },
  { name: 'AlertTriangle', component: AlertTriangle },
  { name: 'Flame', component: Flame },
  { name: 'Settings', component: Settings },
  { name: 'Activity', component: Activity },
  { name: 'FileText', component: FileText },
  { name: 'Phone', component: Phone },
  { name: 'Wind', component: Wind },
  { name: 'Droplets', component: Droplets },
  { name: 'Sun', component: Sun },
  { name: 'Gauge', component: Gauge },
]

export default function CreateTeamModal({ open, setOpen, onSuccess }) {
  const authContext = useContext(AuthContext)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [selectedColor, setSelectedColor] = useState(PRESET_COLORS[0])
  const [selectedIcon, setSelectedIcon] = useState('Users')
  const [addMe, setAddMe] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleClose = () => {
    if (loading) return
    setOpen(false)
  }

  const handleSubmit = async () => {
    if (!name.trim()) {
      setError('Team name is required')
      return
    }

    setLoading(true)
    setError('')

    try {
      await axios(
        `/api/admin/organization/${authContext.user.organization.id}/teams`,
        {
          method: 'POST',
          data: {
            name: name.trim(),
            description: description.trim() || undefined,
            color: selectedColor,
            icon: selectedIcon,
            addCreator: addMe,
          },
        },
        authContext,
      )

      setLoading(false)
      setName('')
      setDescription('')
      setSelectedColor(PRESET_COLORS[0])
      setSelectedIcon('Users')
      setAddMe(true)
      setError('')
      handleClose()

      if (onSuccess) onSuccess()
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create team')
      setLoading(false)
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) handleClose()
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Team</DialogTitle>
          <DialogDescription>
            Create a new team for your organization.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="space-y-2">
            <Label htmlFor="team-name">Team Name *</Label>
            <Input
              id="team-name"
              value={name}
              onChange={e => setName(e.target.value)}
              autoFocus
              aria-invalid={!!error && !name.trim()}
            />
            {error && !name.trim() && (
              <p className="text-xs text-destructive">Team name is required</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="team-description">Description</Label>
            <Textarea
              id="team-description"
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={3}
              placeholder="What is this team for?"
            />
          </div>

          <div>
            <Label className="mb-2 block">Team Color</Label>
            <div className="grid grid-cols-5 gap-2">
              {PRESET_COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  className="h-10 rounded-lg transition-all"
                  style={{
                    backgroundColor: color,
                    border: selectedColor === color ? '3px solid #000' : 'none',
                    boxShadow:
                      selectedColor === color
                        ? `0 0 0 2px white, 0 0 0 4px ${color}`
                        : 'none',
                  }}
                />
              ))}
            </div>
          </div>

          <div>
            <Label className="mb-2 block">Team Icon</Label>
            <div className="grid grid-cols-5 gap-2">
              {TEAM_ICONS.map(
                ({ name: iconName, component: IconComponent }) => (
                  <button
                    key={iconName}
                    type="button"
                    onClick={() => setSelectedIcon(iconName)}
                    className="h-11 flex items-center justify-center rounded-lg transition-all"
                    style={{
                      backgroundColor:
                        selectedIcon === iconName ? selectedColor : '#f3f4f6',
                      color: selectedIcon === iconName ? 'white' : '#6b7280',
                      border:
                        selectedIcon === iconName
                          ? '2px solid #000'
                          : '1px solid #e5e7eb',
                      boxShadow:
                        selectedIcon === iconName
                          ? `0 0 0 2px white, 0 0 0 4px ${selectedColor}`
                          : 'none',
                    }}
                    title={iconName}
                  >
                    <IconComponent size={20} />
                  </button>
                ),
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="add-me"
              checked={addMe}
              onChange={e => setAddMe(e.target.checked)}
              className="size-4 rounded border-input"
            />
            <Label htmlFor="add-me">Add me as a team member</Label>
          </div>

          {error && !!name.trim() && (
            <div className="flex items-start gap-2 rounded-md border border-destructive/20 bg-destructive/5 p-3 text-sm text-destructive">
              <AlertCircle className="size-4 mt-0.5 shrink-0" />
              {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={loading || !name.trim()}>
            {loading ? (
              <>
                <Loader2 className="size-4 animate-spin mr-2" /> Creating...
              </>
            ) : (
              'Create Team'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
